/* Reviews teaser -> live Judge.me popup.
   ES2019 only (FB/IG in-app WebViews) — no optional chaining / nullish coalescing.

   Tapping the teaser opens a <dialog> containing the live shop-wide Judge.me
   cards carousel. The widget is MOVED, not cloned: cloning would copy the
   markup but leave Judge.me's listeners and controls bound to the original, so
   the copy would look right and do nothing. Moving the live node keeps every
   listener attached, because listeners follow the element.

   A comment node marks where the widget lives on the page so it goes back to
   exactly the same spot on close — including if the dialog is dismissed with
   Escape or a backdrop tap.

   If no widget is on the page (app removed, or still loading), we fall back to
   scrolling to where it should be rather than opening an empty box. */
(function () {
  /* The shop-wide cards carousel, preferred over this product's own review
     widget: a product with two reviews makes an empty-looking popup, while the
     carousel always carries the whole store's proof. Falls back to the product
     widget if the carousel block isn't installed on this template. */
  function findWidget() {
    return (
      document.querySelector('.shopify-app-block[id*="cards_carousel"]') ||
      document.getElementById('judgeme_product_reviews') ||
      document.querySelector('.jdgm-review-widget')
    );
  }

  /* Re-centre the carousel after it lands in the dialog.

     This used to dispatch a window 'resize' on the assumption that Judge.me
     measures the carousel from its container. It does not, and the event was
     verified to change nothing: jdgmSetCardsWidth() sets fixed per-breakpoint
     CSS custom properties (--width-xs/sm/md/lg/xl, chosen by the
     reviews_shown setting) and the card width never consults the container.
     So the old call was dead code that read as if it did something.

     What the carousel does compute from live geometry is its translateX, via
     jdgmCarouselCenter.calculateAndApplyTransform. Moving the widget into a
     narrower dialog can leave that translate stale until the next autoplay
     tick, which shows as the active card sitting off-centre and then jumping.
     Nudging Judge.me's own nav one step forward and back forces it to
     recompute against the dialog's real geometry.

     nextCard/previousCard take the carousel's KEY, not its element — they do
     jdgmCarouselMode[key] internally, so passing the node silently no-ops.
     The key is the element's data-jdgm-block-id.

     Guarded on every hop: these are third-party globals and may disappear in
     a Judge.me release. If they are gone we simply skip, exactly as before —
     the carousel still works, it just may re-centre on its own first tick. */
  function remeasure() {
    var nav = window.jdgmCarouselNav;
    if (!nav || typeof nav.nextCard !== 'function' || typeof nav.previousCard !== 'function') return;
    var carousel = document.querySelector('.jdgm-cards-carousel');
    if (!carousel) return;
    var key = carousel.getAttribute('data-jdgm-block-id');
    if (!key || !window.jdgmCarouselMode || !window.jdgmCarouselMode[key]) return;

    /* The nudge must not be visible. Judge.me animates translateX, so a bare
       next+previous plays a full slide-out-and-back on open — measured at
       224px of travel, which is exactly the "it moves when it opens" the
       carousel is being fixed for. Kill the transition for the round trip and
       restore whatever was there afterwards. */
    var wrapper = carousel.querySelector('.jdgm-cards-wrapper');
    var prevTransition = wrapper ? wrapper.style.transition : null;
    if (wrapper) wrapper.style.transition = 'none';
    try {
      nav.nextCard(key);
      nav.previousCard(key);
    } catch (e) {
      /* A signature change upstream must not break opening the dialog. */
    }
    if (wrapper) {
      /* Read a layout property so the transition-less transform is committed
         before the transition goes back on, otherwise the restore coalesces
         into the same frame and animates anyway. */
      void wrapper.offsetWidth;
      wrapper.style.transition = prevTransition || '';
    }
  }

  /* Shop-wide count, so the teaser number and the popup agree. Judge.me puts it
     on the product review widget as data-shop-review-count. */
  function shopReviewCount() {
    var w = document.getElementById('judgeme_product_reviews');
    if (!w) return 0;
    var n = parseInt(w.getAttribute('data-shop-review-count'), 10);
    return isNaN(n) ? 0 : n;
  }

  function init(root) {
    var dialog = root.querySelector('.ct-teaser__reviews');
    var mount = root.querySelector('[data-ct-reviews-mount]');
    var row = root.querySelector('.ct-teaser__row');
    if (!dialog || !mount || !row) return;

    var hasNative = typeof dialog.showModal === 'function';
    var closeBtn = dialog.querySelector('.ct-teaser__reviews-close');
    var home = null; // comment node marking the widget's place on the page
    var widget = null;
    var lastFocus = null;

    var countEl = root.querySelector('[data-ct-count]');
    if (countEl) {
      var total = shopReviewCount();
      var minToShow = parseInt(root.getAttribute('data-min-count'), 10);
      if (isNaN(minToShow)) minToShow = 3;
      if (total >= minToShow) {
        countEl.textContent = total;
        countEl.hidden = false;
      }
    }

    function restore() {
      if (widget && home && home.parentNode) {
        home.parentNode.insertBefore(widget, home);
        home.parentNode.removeChild(home);
      }
      home = null;
      widget = null;
    }

    function cleanup() {
      restore();
      document.body.classList.remove('ct-teaser-lock');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    function close() {
      // Cleanup runs directly, never only via the native 'close' event — some
      // embedded Chromium shells drop that queued event entirely.
      if (hasNative && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      cleanup();
    }

    function open() {
      /* Re-entry would strand the widget: a second open() writes a new home
         marker while the widget already sits in the dialog, orphaning the
         first marker, so close() would "restore" the widget into the dialog
         it just left. showModal() on an open dialog also throws — after the
         DOM was already rearranged. */
      if (dialog.open) return;

      var found = findWidget();

      if (!found) {
        var section = document.querySelector('[id*="judge_me_reviews_review_widget"]');
        if (section && typeof section.scrollIntoView === 'function') {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      lastFocus = document.activeElement;
      widget = found;
      home = document.createComment('judgeme-widget-home');
      widget.parentNode.insertBefore(home, widget);
      mount.appendChild(widget);
      mount.scrollTop = 0;

      if (hasNative) dialog.showModal();
      else dialog.setAttribute('open', '');
      document.body.classList.add('ct-teaser-lock');
      remeasure();
    }

    row.addEventListener('click', open);
    row.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    });

    if (closeBtn) closeBtn.addEventListener('click', close);

    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) close(); // backdrop tap
    });
    dialog.addEventListener('close', cleanup); // safety net; cleanup is idempotent
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault(); // take over from native cancel so the widget always goes home
      close();
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ct-teaser]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
